import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Sparkles, Heart, Lock, HelpCircle, Check, RefreshCw } from 'lucide-react';
import { getBackendUrl } from '../config/api';
import { trackEvent } from '../utils/analytics';



// ── Streak Dots — single source of truth = server streak_days ──────────────────
// Fills exactly `streak` of 7 dots so the dots and the "N Days" label can never
// disagree. (Old version rebuilt dots from history in UTC, which drifted from the
// IST server streak and lost freeze days.) Last filled dot = today (brighter).
const StreakDots = ({ streak = 0 }) => {
  const filled = Math.min(Math.max(streak || 0, 0), 7);
  return (
    <div className="flex gap-1.5 justify-center mt-2" data-testid="diya-streak-dots">
      {Array.from({ length: 7 }, (_, i) => {
        const isFilled = i < filled;
        const isToday = i === filled - 1;
        return (
          <div
            key={i}
            className={`w-2.5 h-2.5 rounded-full border transition-all duration-300 ${
              isFilled
                ? `bg-[color:var(--gold-primary)] border-[color:var(--gold-primary)] ${isToday ? 'shadow-[0_0_9px_rgba(201,168,76,0.9)] scale-125' : 'shadow-[0_0_6px_rgba(201,168,76,0.5)]'}`
                : 'bg-white/5 border-white/10'
            }`}
          />
        );
      })}
    </div>
  );
};

// ── Countdown Hook ───────────────────────────────────────────────────────────
function useCountdown(nextClaimAt) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    if (!nextClaimAt) return;
    const tick = () => {
      const diff = new Date(nextClaimAt) - new Date();
      if (diff <= 0) {
        setLabel('Available now');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLabel(`${h}h ${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [nextClaimAt]);
  return label;
}

// ── Helper to convert timezone offset to IST and check today/yesterday ────────
const getIstTodayAndYesterday = () => {
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const istOffset = 5.5 * 3600000;
  const istNow = new Date(utc + istOffset);
  
  const todayStr = istNow.toISOString().slice(0, 10);
  
  const yesterday = new Date(istNow);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);
  
  return { todayStr, yesterdayStr, istNow };
};

const getAuthToken = () =>
  typeof window !== 'undefined'
    ? (localStorage.getItem('jyotish_session_token') ||
       localStorage.getItem('token') ||
       sessionStorage.getItem('token') ||
       null)
    : null;

// EN/HI copy follows the same `jyotish_report_lang` convention the predictions
// and report surfaces use (PredictionsDashboard.jsx:96-102). Defaults to English.
const resolveLanguage = () => {
  try {
    return localStorage.getItem('jyotish_report_lang') === 'hi' ? 'hi' : 'en';
  } catch {
    return 'en';
  }
};

// S-1: welcome-back line shown after a claim that restarted a lapsed streak. The
// day count and karma value come from the server response (streak_days +
// next_bonus), never hard-coded (invariant: product claims trace to a fact).
const streakResetLine = (language, streakDays, nextBonus) => {
  const day = streakDays || 1;
  const toDay = nextBonus?.day;
  const value = nextBonus?.value;
  if (language === 'hi') {
    const tail = (toDay && value) ? ` ${toDay} din mein ${value} karma bonus.` : '';
    return `Wapas swagat hai — nayi streak ka Din ${day}.${tail}`;
  }
  const tail = (toDay && value) ? ` ${toDay} days to your ${value}-karma bonus.` : '';
  return `Welcome back — Day ${day} of a fresh streak.${tail}`;
};

// ── DiyaPrasadCard Component ────────────────────────────────────────────────
export default function DiyaPrasadCard({ userInfo, onRewardClaimed, onNavigate }) {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [rewardResult, setRewardResult] = useState(null);

  const language = resolveLanguage();
  const countdown = useCountdown(status?.next_claim_at || rewardResult?.next_claim_at);

  const fetchStatus = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setLoading(false);
      return;
    }
    try {
      const [statusRes, histRes] = await Promise.all([
        fetch(`${getBackendUrl()}/api/rewards/status`, { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch(`${getBackendUrl()}/api/rewards/history?limit=7`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      if (statusRes.ok) {
        const data = await statusRes.json();
        setStatus(data);
      }
      if (histRes.ok) {
        const h = await histRes.json();
        setHistory(h.history || []);
      }
    } catch (e) {
      console.error('[DiyaPrasadCard] fetch status error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Track viewed event
    trackEvent('diya_card_viewed', { user_id: userInfo?.user_id });
  }, [fetchStatus, userInfo?.user_id]);

  // Handle the daily Prasad claim. S-1: the claim always succeeds — a missed day
  // no longer prompts a freeze/reset interstitial, so there are no claim options.
  const handleClaimPrasad = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setClaimError('Session expired. Please sign in again.');
      return;
    }

    setClaiming(true);
    setClaimError(null);
    trackEvent('diya_claim_tapped', { user_id: userInfo?.user_id });

    try {
      const res = await fetch(`${getBackendUrl()}/api/rewards/daily-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (!res.ok || data.success === false) {
        setClaimError(data.message || data.error || 'Claim failed.');
        setClaiming(false);
        return;
      }

      // Success!
      setRewardResult(data);
      setStatus(prev => ({
        ...prev,
        claimed_today: true,
        streak_days: data.streak_days,
        next_claim_at: data.next_claim_at
      }));

      // Notify parent to refresh wallet/karma balance
      if (onRewardClaimed) {
        onRewardClaimed();
      }
      
      // Refresh local history lists
      const histRes = await fetch(`${getBackendUrl()}/api/rewards/history?limit=7`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (histRes.ok) {
        const h = await histRes.json();
        setHistory(h.history || []);
      }
    } catch (e) {
      console.error('[DiyaPrasadCard] claim failed:', e);
      setClaimError('Network error. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  // State Machine Derivations
  const isClaimed = status?.claimed_today || rewardResult?.already_claimed;
  
  // Derive AT_RISK status
  const getAtRiskState = () => {
    if (isClaimed) return false;
    const { yesterdayStr, istNow } = getIstTodayAndYesterday();
    const isAfter20 = istNow.getHours() >= 20;
    const yesterdayClaimed = (history || []).some(h => h.claim_date === yesterdayStr);
    return isAfter20 && yesterdayClaimed;
  };

  const isAtRisk = getAtRiskState();

  // Card states — S-1 removed the BROKEN state (a missed day now auto-restarts
  // the streak on claim and shows a welcome-back line, no interstitial).
  let cardState = 'UNCLAIMED';
  if (isClaimed) cardState = 'CLAIMED';
  else if (isAtRisk) cardState = 'AT_RISK';

  // SVG Diya lit status & flicker control
  const isLit = cardState === 'CLAIMED' || cardState === 'AT_RISK';
  const shouldFlicker = cardState === 'AT_RISK';

  if (loading) {
    return (
      <div className="jp-card rounded-2xl p-6 border border-white/10 flex items-center justify-center min-h-[140px]">
        <div className="animate-spin w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`jp-card rounded-2xl p-5 border relative overflow-hidden transition-all duration-300 ${
      cardState === 'AT_RISK'
        ? 'border-red-500/50 bg-gradient-to-br from-[#1c0812] to-[#12061e] shadow-[0_0_25px_rgba(239,68,68,0.15)]'
        : cardState === 'CLAIMED'
        ? 'border-[rgba(201,168,76,0.4)] bg-gradient-to-br from-[#2a1c47] via-[#231640] to-[#1a1136] shadow-[0_0_30px_rgba(201,168,76,0.18)]'
        : 'border-white/10 bg-gradient-to-br from-[#16122c] to-[#0a0515] hover:border-white/20'
    }`} data-testid="diya-prasad-card">
      
      {/* Decorative Aura */}
      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-30 pointer-events-none transition-all duration-500 ${
        cardState === 'AT_RISK'
          ? 'bg-red-500'
          : cardState === 'CLAIMED'
          ? 'bg-amber-400'
          : 'bg-purple-600'
      }`} />

      <div className="flex flex-col md:flex-row items-center gap-5 justify-between relative z-10">
        
        {/* Left Side: SVG Diya and status title */}
        <div className="flex items-center gap-4 flex-1 w-full">
          <div className="flex-shrink-0 relative">
            <svg viewBox="0 0 100 80" className="w-20 h-16 md:w-24 md:h-20 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
              <defs>
                <radialGradient id="flameGrad" cx="50%" cy="80%" r="50%">
                  <stop offset="0%" stopColor="#ef4444" />
                  <stop offset="50%" stopColor="#f97316" />
                  <stop offset="100%" stopColor="#eab308" stopOpacity="0.2" />
                </radialGradient>
              </defs>
              {/* Diya base (clay pot style) */}
              <path d="M15,45 C15,65 35,75 50,75 C65,75 85,65 85,45 C85,45 70,50 50,50 C30,50 15,45 15,45 Z" fill="#b45309" stroke="#78350f" strokeWidth="1.5" />
              <ellipse cx="50" cy="47" rx="35" ry="5" fill="#78350f" opacity="0.8" />
              {/* Oil inside */}
              <ellipse cx="50" cy="47" rx="30" ry="3" fill="#eab308" opacity="0.6" />
              {/* Cotton wick */}
              <path d="M 50 47 Q 52 35 50 30" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
              
              {/* Flame (only if lit/flickering) */}
              {isLit && (
                <motion.g
                  animate={shouldFlicker ? {
                    scale: [1, 1.15, 0.9, 1.1, 1],
                    y: [0, -2, 2, -3, 0],
                    x: [0, 1, -1, 2, 0],
                    opacity: [0.8, 1, 0.75, 1, 0.8]
                  } : {
                    scale: [1, 1.05, 0.97, 1.02, 1],
                    y: [0, -1, 1, -1, 0],
                    opacity: [0.95, 1, 0.92, 1, 0.95]
                  }}
                  transition={{ repeat: Infinity, duration: shouldFlicker ? 0.35 : 1.3, ease: "easeInOut" }}
                >
                  {/* Flame main */}
                  <path d="M50,30 C45,24 44,14 50,4 C56,14 55,24 50,30 Z" fill="url(#flameGrad)" filter="drop-shadow(0 0 8px rgba(249,115,22,0.7))" />
                  {/* Flame core */}
                  <path d="M50,30 C47,27 47,19 50,12 C53,19 53,27 50,30 Z" fill="#eab308" opacity="0.9" />
                </motion.g>
              )}
            </svg>
          </div>

          <div className="text-left flex-1 min-w-0">
            {cardState === 'UNCLAIMED' && (
              <>
                <p className="text-[color:var(--gold-primary)] text-[10px] font-semibold tracking-wider uppercase">॥ आज का प्रसाद ॥</p>
                <h3 className="text-white text-base font-bold mt-0.5">Aaj ka Prasad</h3>
                <p className="text-gray-400 text-xs mt-0.5">Claim your daily cosmic reward to keep your streak alive.</p>
              </>
            )}

            {cardState === 'AT_RISK' && (
              <>
                <div className="flex items-center gap-1.5 text-red-400">
                  <Flame className="w-3.5 h-3.5 animate-bounce" />
                  <p className="text-[10px] font-bold tracking-wider uppercase">Devotion At Risk</p>
                </div>
                <h3 className="text-white text-base font-bold mt-0.5">Devotion at Risk!</h3>
                <p className="text-red-300/80 text-xs mt-0.5">Your streak breaks in <span className="font-semibold text-amber-400">{countdown}</span>. Claim immediately!</p>
              </>
            )}

            {cardState === 'CLAIMED' && (
              <>
                <p className="text-emerald-400 text-[10px] font-bold tracking-wider uppercase flex items-center gap-1">
                  <Check className="w-3 h-3" /> claimed
                </p>
                <h3 className="text-white text-base font-bold mt-0.5">Prasad Claimed</h3>
                
                {/* Reward content */}
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-xl font-extrabold text-[color:var(--gold-primary)]">{rewardResult?.reward_value || status?.active_discount?.discount_percent || status?.active_discount?.code || '✦'}</span>
                  <span className="text-xs text-gray-300">{rewardResult?.reward_label || 'Devotional Reward'}</span>
                </div>

                {/* S-1: welcome-back line when this claim restarted a lapsed streak */}
                {rewardResult?.streak_reset && (
                  <p
                    className="text-emerald-300 text-xs font-medium mt-1.5 max-w-[280px] leading-relaxed"
                    data-testid="streak-reset-line"
                  >
                    {streakResetLine(language, rewardResult.streak_days, rewardResult.next_bonus)}
                  </p>
                )}

                {/* Blessing message */}
                {(rewardResult?.blessing_message || status?.active_discount?.blessing_message) && (
                  <p className="text-gray-400 text-[11px] italic mt-1 max-w-[280px] border-l border-white/10 pl-2 leading-relaxed">
                    "{rewardResult?.blessing_message || status?.active_discount?.blessing_message}"
                  </p>
                )}

                {/* Use it now — Ask ChitraGupt CTA */}
                <button
                  onClick={() => onNavigate?.('chat')}
                  className="mt-2 text-xs font-bold text-amber-300 hover:text-amber-200 flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
                >
                  Use it now — Ask ChitraGupt →
                </button>

                {/* Day n of 7 → bonus line */}
                <p className="text-[10px] text-gray-500 mt-1">
                  Day {Math.min(status?.streak_days || rewardResult?.streak_days || 1, 7)} of 7 → bonus
                </p>

                {/* Milestone details from backend if exist */}
                {rewardResult?.streak_bonus && (
                  <p className="text-xs text-[color:var(--gold-primary)] font-semibold mt-1">
                    🙏 Streak Milestone Reached! Bonus: {rewardResult.streak_bonus}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Side: Interactive buttons, streak counter & history */}
        <div className="flex flex-col items-center md:items-end gap-2 w-full md:w-auto">
          {cardState === 'UNCLAIMED' && (
            <button
              onClick={() => handleClaimPrasad()}
              disabled={claiming}
              data-testid="accept-prasad-btn"
              className="w-full md:w-32 py-2 rounded-lg font-bold text-xs bg-gradient-to-r from-[var(--gold-primary)] to-[#E8C96A] text-[#0A0516] hover:from-[#E8C96A] hover:to-[var(--gold-primary)] transition shadow-[0_4px_15px_rgba(201,168,76,0.25)] flex items-center justify-center gap-1 disabled:opacity-60"
            >
              {claiming ? 'Claiming...' : 'Claim Prasad'}
            </button>
          )}

          {cardState === 'AT_RISK' && (
            <button
              onClick={() => handleClaimPrasad()}
              disabled={claiming}
              data-testid="accept-prasad-btn"
              className="w-full md:w-32 py-2 rounded-lg font-bold text-xs bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-orange-500 hover:to-red-500 transition shadow-[0_4px_15px_rgba(239,68,68,0.3)] flex items-center justify-center gap-1 disabled:opacity-60 animate-pulse"
            >
              {claiming ? 'Claiming...' : 'Claim Prasad'}
            </button>
          )}

          {cardState === 'CLAIMED' && (
            <div className="text-center md:text-right">
              <span className="text-[10px] text-gray-500 tracking-wider block">NEXT PRASAD IN</span>
              <span className="text-white text-xs font-bold font-mono tracking-wide">{countdown || 'Tomorrow'}</span>
            </div>
          )}

          {/* Show error if any */}
          {claimError && (
            <p className="text-red-400 text-[10px] text-center md:text-right max-w-[200px] leading-tight">
              {claimError}
            </p>
          )}

          {/* Streak count & 7-day history */}
          <div className="mt-1.5 text-center md:text-right">
            <span className="text-[9px] text-gray-500 tracking-wider block">CURRENT STREAK</span>
            <span className="text-white text-xs font-bold font-mono block">
              {status?.streak_days || 0} {status?.streak_days === 1 ? 'Day' : 'Days'} 🔥
            </span>
            <StreakDots streak={status?.streak_days ?? rewardResult?.streak_days ?? 0} />
          </div>
        </div>
      </div>
    </div>
  );
}
